import React, { useState, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../../../../components/Common/UI/Button';
import { Icons } from '../../../../../../components/Common/Icons/Icons';
import DataTable from '../../../../../../components/Common/UI/DataTable';

interface AuthorizedViewer {
  id: string;
  name: string;
  email: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  securityQuestion?: string;
  securityAnswer?: string;
  selectedAccounts?: string[];
}

const initialData: AuthorizedViewer[] = [
  {
    id: '1',
    name: 'john_doe',
    email: 'john_doe@infoimageinc.com',
    firstName: 'John',
    lastName: 'Doe',
    username: 'john_doe',
    securityQuestion: 'zero',
    securityAnswer: 'New York',
    selectedAccounts: ['****4560', '****4564'],
  },
  {
    id: '2',
    name: 'jenny_smith',
    email: 'jenny_smith@infoimageinc.com',
    firstName: 'Jenny',
    lastName: 'Smith',
    username: 'jenny_smith',
    securityQuestion: 'one',
    securityAnswer: 'Johnson',
    selectedAccounts: ['****4560', '****4564'],
  },
  {
    id: '3',
    name: 'alex_smith',
    email: 'alex_smith@infoimageinc.com',
    firstName: 'Alex',
    lastName: 'Smith',
    username: 'alex_smith',
    securityQuestion: 'two',
    securityAnswer: 'Buddy',
    selectedAccounts: ['****4560', '****4564'],
  },
  {
    id: '4',
    name: 'alex_smith2',
    email: 'alex_smith@infoimageinc.com',
    firstName: 'Alex',
    lastName: 'Smith',
    username: 'alex_smith2',
    securityQuestion: 'zero',
    securityAnswer: 'Boston',
    selectedAccounts: ['****4560', '****4564'],
  },
];

export default function AuthorizedViewersTable() {
  const navigate = useNavigate();
  const [viewers, setViewers] = useState<AuthorizedViewer[]>(initialData);

  useEffect(() => {
    const loadTempData = () => {
      const tempViewers = JSON.parse(sessionStorage.getItem('tempViewers') || '[]');
      const viewerUpdates = JSON.parse(sessionStorage.getItem('viewerUpdates') || '[]');

      setViewers(prev => {
        let result = [...initialData];

        const existingIds = new Set(result.map(v => v.id));
        const newViewers = tempViewers.filter((v: AuthorizedViewer) => !existingIds.has(v.id));

        result = [...result, ...newViewers];

        viewerUpdates.forEach((update: AuthorizedViewer) => {
          result = result.map(v => (v.id === update.id ? update : v));
        });

        return result;
      });
    };

    loadTempData();
    window.addEventListener('focus', loadTempData);

    return () => window.removeEventListener('focus', loadTempData);
  }, []);

  const handleEdit = (viewer: AuthorizedViewer) => {
    navigate('/guidance/create-authorized-viewers', {
      state: { viewer, fromEdit: true },
    });
  };

  const handleRemove = (id: string) => {
    setViewers(prev => prev.filter(v => v.id !== id));
  };

  const columns: ColumnDef<AuthorizedViewer>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
    },
    {
      accessorKey: 'email',
      header: 'Email',
    },
    {
      id: 'actions',
      header: 'Action',
      enableSorting: false,
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: ({ row }) => {
        const viewer = row.original;

        return (
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              className="flex items-center gap-1 text-red-500 hover:text-red-700 text-sm font-medium"
              onClick={() => handleRemove(viewer.id)}
            >
              <Icons.DeleteIconNew className="w-4 h-4 text-red-500" />
              Remove
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="text-addition7 hover:text-addition7"
              onClick={() => handleEdit(viewer)}
            >
              <Icons.EditPen className="w-4 h-4 mr-1 text-addition7" />
              Edit Profile
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={viewers}
      variant="default"
      paginationPosition="bottom"
      emptyText="No authorized viewers"
      paginationVariant="guidance"
    />
  );
}
